"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Cog, Microscope, Workflow, Users, Layers, Cpu, ArrowRight, Sparkles, Lightbulb } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const SOLUTIONS = [
  { icon: Cog, title: "Systems Over Values", body: "Excellence must be engineered into protocols, checklists, reviews, and outcome accountability — the mechanisms that produce repeatable results.", accent: "red" },
  { icon: Microscope, title: "Diagnosis Before Intervention", body: "Forensic diagnosis — pattern identification, anomaly detection, root-cause analysis — before any intervention. The clinical method applied to organizations.", accent: "purple" },
  { icon: Users, title: "Hiring the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the market is mis-pricing. The 70% Operating Model proves that selection optimizes for the talent pool everyone overlooks.", accent: "lime" },
  { icon: Layers, title: "The Integrated Operating Method", body: "Sixty-plus integrated services — from real-time intelligence through governance delivery — operating as a unified system under a single command. Integration is the moat.", accent: "cyan" },
  { icon: Workflow, title: "The Clinical Discipline", body: "Diagnose. Triage. Run the protocol. Keep the judgment. Own the outcome. Do no harm. Six principles, learned in a delivery room, proven across eighteen countries.", accent: "amber-gold" },
];

const TEXT: Record<string, string> = {
  red: "text-red", purple: "text-purple", lime: "text-lime", cyan: "text-cyan", "amber-gold": "text-amber-gold",
};

export function SolutionSection() {
  return (
    <Section id="solution" ariaLabel="The Solution She Provides" background="muted">
      <SectionHeader
        icon={Lightbulb}
        overline="The Solution"
        overlineClassName="text-emerald"
        pillClassName="glass-tinted-emerald border-emerald/20"
        title={
          <>
            Operational Excellence as an <span className="text-gradient-success">Engineered System</span>
          </>
        }
        subtitle="The solution is not more strategy. It is not more meetings. It is a method — clinical, systematized, accountable — to making excellent things actually run."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {SOLUTIONS.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group relative glass-card rounded-2xl p-6 overflow-hidden border border-border/30"
          >
            <div aria-hidden className="absolute -top-16 -right-16 size-48 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
            <div className="relative">
              <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg mb-5 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                <s.icon className="size-6 text-white" />
              </div>
              <h3 className={`text-heading-md mb-3 ${TEXT[s.accent]}`}>{s.title}</h3>
              <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{s.body}</p>
            </div>
          </motion.div>
        ))}

        {/* CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="group relative rounded-2xl p-6 overflow-hidden bg-gradient-sovereign text-white flex flex-col justify-center"
        >
          <Sparkles className="size-10 mb-4 animate-pulse-soft" />
          <h3 className="text-heading-lg mb-3">Engage the operator.</h3>
          <p className="text-body-sm opacity-80 leading-relaxed mb-5">
            When the outcome must be operational excellence — not a strategy deck, but actual, sustained, verified execution — there is one operator who has built for exactly this.
          </p>
          <Link href="/contact/" className="inline-flex items-center gap-2 text-body-sm font-semibold hover:gap-3 transition-all">
            Begin the conversation <ArrowRight className="size-4" />
          </Link>
        </motion.div>
      </div>
    </Section>
  );
}
